import { useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { EARN_METHODS } from '@/data/rewardsData';
import { Sparkles, Users, Gift, Star, Flame, Camera, CheckCircle, Image } from 'lucide-react';

const iconMap = {
  Sparkles, Users, Gift, Star, Flame, Camera, CheckCircle, Image,
};

/* Each card gets a unique subtle accent color for more visual richness */
const cardAccents = [
  { border: 'hsl(40 76% 44% / 0.25)', glow: 'hsl(40 76% 44% / 0.08)', icon: 'hsl(40 76% 52%)', badge: 'hsl(40 76% 44%)' },
  { border: 'hsl(195 50% 70% / 0.25)', glow: 'hsl(195 50% 82% / 0.08)', icon: 'hsl(195 50% 75%)', badge: 'hsl(195 50% 82%)' },
  { border: 'hsl(345 40% 50% / 0.2)', glow: 'hsl(345 40% 50% / 0.06)', icon: 'hsl(345 40% 60%)', badge: 'hsl(345 40% 55%)' },
  { border: 'hsl(155 35% 45% / 0.2)', glow: 'hsl(155 35% 45% / 0.06)', icon: 'hsl(155 35% 55%)', badge: 'hsl(155 35% 50%)' },
  { border: 'hsl(270 30% 55% / 0.2)', glow: 'hsl(270 30% 55% / 0.06)', icon: 'hsl(270 30% 62%)', badge: 'hsl(270 30% 58%)' },
  { border: 'hsl(25 60% 50% / 0.2)', glow: 'hsl(25 60% 50% / 0.06)', icon: 'hsl(25 60% 60%)', badge: 'hsl(25 60% 55%)' },
  { border: 'hsl(215 45% 50% / 0.2)', glow: 'hsl(215 45% 50% / 0.06)', icon: 'hsl(215 45% 60%)', badge: 'hsl(215 45% 55%)' },
  { border: 'hsl(42 55% 60% / 0.2)', glow: 'hsl(42 55% 60% / 0.06)', icon: 'hsl(42 55% 68%)', badge: 'hsl(42 55% 65%)' },
];

const EarnCard = ({ method, index }) => {
  const cardRef = useRef(null);
  const Icon = iconMap[method.icon];
  const accent = cardAccents[index] || cardAccents[0];

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    cardRef.current.style.setProperty('--mouse-x', `${x}px`);
    cardRef.current.style.setProperty('--mouse-y', `${y}px`);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
    >
      <Card
        ref={cardRef}
        onMouseMove={handleMouseMove}
        className="relative h-full bg-gradient-card border rounded-xl p-6 overflow-hidden group transition-all duration-500 flex flex-col"
        style={{
          borderColor: 'hsl(var(--border) / 0.4)',
        }}
      >
        {/* Colored top edge accent line */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: `linear-gradient(90deg, transparent, ${accent.border}, transparent)` }}
        />

        {/* Spotlight effect on hover */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl pointer-events-none"
          style={{
            background: `radial-gradient(300px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), ${accent.glow}, transparent 60%)`,
          }}
        />

        {/* Hover border glow */}
        <div
          className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{ boxShadow: `inset 0 0 0 1px ${accent.border}` }}
        />

        <div className="relative flex-1 flex flex-col">
          <div className="flex items-start justify-between mb-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
              style={{
                background: `${accent.glow}`,
                border: `1px solid ${accent.border}`,
              }}
            >
              {Icon && <Icon className="w-5 h-5" style={{ color: accent.icon }} />}
            </div>
            <span
              className="font-body text-xs px-3 py-1.5 rounded-full border"
              style={{
                background: `${accent.badge}10`,
                borderColor: `${accent.badge}20`,
                color: accent.badge,
              }}
            >
              {method.gp}
            </span>
          </div>
          <h3 className="font-display text-lg text-foreground mb-2 group-hover:text-gold transition-colors duration-300">{method.title}</h3>
          <p className="font-body text-xs text-muted-foreground leading-relaxed flex-1">{method.desc}</p>
        </div>
      </Card>
    </motion.div>
  );
};

export const EarnSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="earn" ref={ref} className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background with subtle color wash */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 0%, hsl(var(--card) / 0.3) 50%, transparent 100%)' }} />
      {/* Subtle colored orbs for richness */}
      <div className="absolute top-20 left-10 w-80 h-80 rounded-full opacity-[0.03]" style={{ background: 'radial-gradient(circle, hsl(195 50% 82%), transparent)' }} />
      <div className="absolute bottom-20 right-10 w-60 h-60 rounded-full opacity-[0.04]" style={{ background: 'radial-gradient(circle, hsl(345 40% 50%), transparent)' }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Royal ornamental divider */}
        <div className="flex items-center gap-3 justify-center mb-10">
          <div className="h-px w-16 bg-gold/20" />
          <svg width="16" height="16" viewBox="0 0 20 20" className="text-gold/40">
            <path d="M10 0L12 8L20 10L12 12L10 20L8 12L0 10L8 8Z" fill="currentColor" />
          </svg>
          <div className="h-px w-16 bg-gold/20" />
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 lg:mb-20"
        >
          <span className="font-body text-xs tracking-[0.3em] uppercase text-gold mb-4 block">Earn Glow Points</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-foreground font-medium leading-tight">
            More Ways to <span className="text-shimmer-gold italic">Glow</span>
          </h2>
          <p className="font-body text-base text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed">
            Beyond treatments — earn Glow Points through referrals, reviews, streaks, and community engagement.
          </p>
        </motion.div>

        {/* Earn Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {EARN_METHODS.map((method, i) => (
            <EarnCard key={method.title} method={method} index={i} />
          ))}
        </div>

        {/* Conversion callout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-gold/20 bg-gold/5">
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="font-body text-sm text-foreground">Conversion: <span className="text-gold font-medium">1,000 GP = ₹500</span> off any treatment</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
